import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * RIS_BASIC_01 스키마
 * 위험성평가(RIS_BASIC_01) 모듈의 데이터 스키마 및 필드 매퍼
 * 스키마 기반 데이터 변환을 위한 MEM_BASIC 패턴을 따름
 */
export const RIS_BASIC_01_Schema = {
  /**
   * 필드 메타데이터
   * 모든 폼 필드의 초기값, 레이블, 검증 규칙을 정의
   */
  fields: {
    // 기본정보
    rsCd: { initial: '', label: '위험성평가 코드' },
    custCd: { initial: '', label: '사업장코드' },
    depCd: { initial: '', label: '부서코드' },
    rsNm: { initial: '', label: '위험성 평가명' },
    rsGubun: { initial: '정기평가', label: '평가구분' },
    rsMethod: {
      initial: '위험성 수준 3단계 판단법',
      label: '평가방법',
    },
    rsDate: {
      initial: () => dayjs().format('YYYY-MM-DD'),
      label: '평가일시',
    },
    rsMember: { initial: '', label: '평가자' },
    workType: { initial: '', label: '작업종류' },
    product: { initial: '', label: '생산품' },
    wokerCount: { initial: '', label: '근로자수' },
    rawMaterial: { initial: '', label: '원(재료)' },

    // 파일 정보
    fileName: { initial: '', label: '파일명' },
    saveName: { initial: '', label: '저장파일명' },
    filePath: { initial: '', label: '파일경로' },

    // 기타 안전보건 정보
    otherSafetyInformation: { initial: '', label: '기타 안전보건정보' },

    // 위험성평가 실시 - 공통
    rsdCd: { initial: '', label: '위험성평가 상세 코드' },
    categoryComment: {
      initial: '',
      label: '위험발생 상황 및 결과',
    },
    todaySafety: { initial: '', label: '현재의 안전보건조치' },
    reductionMeasures: { initial: '', label: '위험성감소대책' },
    gaesunExDate: { initial: '', label: '개선예정일' },
    gaesunComDate: { initial: '', label: '개선완료일' },
    riskId: { initial: '', label: '위험성ID' },
    condolenceNo: { initial: '', label: '사고번호' },
    relatedEvidence: { initial: '', label: '관련근거' },

    // 위험성평가 실시 - 3단계 판단법
    riskLevelJudgment: { initial: '', label: '위험성 수준 판단' },
    riskAfterLevelJudgment: { initial: '', label: '개선 후 위험성 수준' },

    // 위험성평가 실시 - 빈도·강도법
    todayPossibility: {
      initial: '',
      label: '가능성(빈도)',
      type: 'number',
    },
    todayImportance: {
      initial: '',
      label: '중대성(강도)',
      type: 'number',
    },
    todayCurrentRisk: { initial: '', label: '위험성', type: 'number' },
    afterPossibility: { initial: '', label: '개선 후 가능성', type: 'number' },
    afterImportance: { initial: '', label: '개선 후 중대성', type: 'number' },
    afterCurrentRisk: { initial: '', label: '개선 후 위험성', type: 'number' },

    // 주요공정
    srgId: { initial: '', label: '공정ID' },
    processNo: { initial: '', label: '공정번호' },
    gongJongNm: { initial: '', label: '공정명' },

    // 화학물질
    srcId: { initial: '', label: '화학물질ID' },
    chemicals: { initial: '', label: '화학물질명' },
    maximumStorage: { initial: '', label: '최대저장량' },
    maximumDaily: { initial: '', label: '1일최대사용량' },
    unit: { initial: '', label: '단위' },

    // 유해위험요인
    sfhId: { initial: '', label: '유해위험요인ID' },
    mainWork: { initial: '', label: '주요작업' },
    harmfulRiskMachine: { initial: '', label: '유해위험기계' },
    potentialRisk: { initial: '', label: '잠재적위험' },
  },

  /**
   * API 매퍼
   * 모든 API 호출을 위한 스키마 기반 변환 함수
   * 각 매퍼는 UI 형식과 API 형식 간의 데이터 변환을 수행
   */
  mapper: {
    /**
     * 기본 조회 (SP_RIS_BASIC_01_R1)
     * 기본 위험성평가 목록 조회
     */
    search1: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      StartDate: (_, context) =>
        Commons.EncryptAES(context.search?.startDate || ''),
      EndDate: (_, context) =>
        Commons.EncryptAES(context.search?.endDate || ''),
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || ''),
    },

    /**
     * 안전보건정보 조회 (SP_RIS_BASIC_01_R2)
     * 안전보건정보 조회
     */
    search2: {
      RsCd: (_, context) => Commons.EncryptAES(context.rsCd || ''),
    },

    /**
     * 부서조회
     */
    search3: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      SearchText: (_, context) => Commons.EncryptAES(''),
    },

    /**
     * 부서별 공정 조회
     */
    search4: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
    },

    /**
     * 안전보건정보 안 모든 설비 , 제품 조회
     */
    search5: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      Year: (_, context) =>
        Commons.EncryptAES(dayjs(context.search?.endDate).format('YYYY') || ''),
    },
    /**
     * 위험성평가 실시 안 작업 목록 조회
     */
    search6: {
      RsCd: (_, context) => Commons.EncryptAES(context.rsCd || ''),
    },
    /**
     * 위험성평가 실시 안 표준모델요인 조회
     */
    search7: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || ''),
    },
    /**
     * 위험성평가 기본정보 저장
     */
    save1: {
      Request: (row) => Commons.EncryptAES(row.request),
      UserId: (_, context) => context.userInfo?.userid,
    },
    /**
     * 위험성평가 기본정보 안 공정등록
     */
    save2: {
      DepCd: (row) => Commons.EncryptAES(row.depCd),
      GongJongNm: (row) => Commons.EncryptAES(row.gongJongNm),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      UserId: (_, context) => context.userInfo?.userid,
    },
    /**
     * 위험성평가 실시 저장
     */
    save3: {
      RsdCd: (row) => Commons.EncryptAES(row.rsdCd ?? ''),
      SubJobId: (row) =>
        Commons.EncryptAES(
          row.subJobId != null && row.subJobId !== ''
            ? String(row.subJobId)
            : ''
        ),
      RsCd: (row) => Commons.EncryptAES(row.rsCd ?? ''),
      DepCd: (row) => Commons.EncryptAES(row.depCd ?? ''),
      CategoryComment: (row) =>
        Commons.EncryptAES(row.categoryComment ?? ''),
      TodaySafety: (row) => Commons.EncryptAES(row.todaySafety ?? ''),
      ReductionMeasures: (row) =>
        Commons.EncryptAES(row.reductionMeasures ?? ''),
      RiskLevelJudgment: (row) =>
        Commons.EncryptAES(row.riskLevelJudgment ?? ''),
      GaesunExDate: (row) => Commons.EncryptAES(row.gaesunExDate ?? ''),
      GaesunComDate: (row) => Commons.EncryptAES(row.gaesunComDate ?? ''),
      RiskId: (row) => Commons.EncryptAES(row.riskId ?? ''),
      RiskAfterLevelJudgment: (row) =>
        Commons.EncryptAES(row.riskAfterLevelJudgment ?? ''),
      CondolenceNo: (row) => Commons.EncryptAES(row.condolenceNo ?? ''),
      RelatedEvidence: (row) => Commons.EncryptAES(row.relatedEvidence ?? ''),
      TodayPossibility: (row) =>
        Commons.EncryptAES(row.todayPossibility ?? ''),
      TodayImportance: (row) =>
        Commons.EncryptAES(row.todayImportance ?? ''),
      TodayCurrentRisk: (row) =>
        Commons.EncryptAES(row.todayCurrentRisk ?? ''),
      AfterPossibility: (row) =>
        Commons.EncryptAES(row.afterPossibility ?? ''),
      AfterImportance: (row) =>
        Commons.EncryptAES(row.afterImportance ?? ''),
      AfterCurrentRisk: (row) =>
        Commons.EncryptAES(row.afterCurrentRisk ?? ''),
      ProcessStatus: (row) => Commons.EncryptAES(row.processStatus ?? ''),
      RiskImageFileName: (row) =>
        Commons.EncryptAES(row.riskImageFileName ?? ''),
      RiskImageSaveName: (row) =>
        Commons.EncryptAES(row.riskImageSaveName ?? ''),
      RiskImageFilePath: (row) =>
        Commons.EncryptAES(row.riskImageFilePath ?? ''),
      AfterRiskImageFilePath: (row) =>
        Commons.EncryptAES(row.afterRiskImageFilePath ?? ''),
      AfterRiskImageSaveName: (row) =>
        Commons.EncryptAES(row.afterRiskImageSaveName ?? ''),
      AfterRiskImageFileName: (row) =>
        Commons.EncryptAES(row.afterRiskImageFileName ?? ''),
      UserId: (_, context) => context.userInfo?.userid,
    },
    /**
     * 위험성평가 삭제 (SP_RIS_BASIC_01_D1)
     * 위험성평가 삭제
     */
    delete1: {
      RsCd: (row) => Commons.EncryptAES(row.rsCd || ''),
      UserId: (_, context) => context.userInfo?.userid,
    },
  },

  /**
   * 검증 규칙
   * 다양한 폼에 대한 필수 필드 그룹
   */
  validation: {
    // 기본정보 필수 항목
    basicInfo: [
      { field: 'RS_NM', name: '위험성 평가명' },
      { field: 'RS_GUBUN', name: '평가구분' },
      { field: 'RS_METHOD', name: '평가방법' },
      { field: 'RS_DATE', name: '평가일시' },
      { field: 'depCd', name: '부서' },
    ],

    // 위험성평가 실시 - 3단계 판단법
    riskAssessment3Level: [
      { field: 'CATEGORY_COMMENT', name: '위험발생 상황 및 결과' },
    ],

    // 위험성평가 실시 - 빈도강도법
    riskAssessmentFrequency: [
      { field: 'CATEGORY_COMMENT', name: '위험발생 상황 및 결과' },
      { field: 'TODAY_POSSIBILITY', name: '가능성(빈도)' },
      { field: 'TODAY_IMPORTANCE', name: '중대성(강도)' },
    ],
  },
};

export default RIS_BASIC_01_Schema;
